import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button, Select } from 'antd';
import { DollarOutlined, PlusOutlined, CreditCardOutlined } from '@ant-design/icons';
import paymentsActions from 'redux/paymentsSpa/actions';
import Can from 'config/Can';
import useTranslation from 'utils/hooks/useTranslation';
import useAmplitude from 'utils/hooks/useAmplitude';
import { isUkCorporationCreatedAfterWalletGoLive, toAnalyticsCurrency } from 'utils/walletUtil';
import { AMPLITUDE_EVENT_TYPES, AMPLITUDE_WALLET_PROPERTY_VALUES } from 'constants/amplitude';
import {
  CARD_LOAD_DEFAULT_PRESET_MAJOR,
  CARD_LOAD_PRESETS_MAJOR,
  WALLET_BANK_TRANSFER_DETAILS,
  WALLET_CURRENCY_SYMBOLS,
} from 'constants/wallet';
import { toMinorUnits } from 'pages/Wallet/CreateWallet/helper';
import AmountInput from './AmountInput';
import SendTransferModal from './SendTransferModal';
import { BANK_STEPS } from './useBankTransferFlow';
import ConfirmTransferModal from './ConfirmTransferModal';
import AddCardModal from './AddCardModal';
import ConfirmCardPaymentModal from './ConfirmCardPaymentModal';
import {
  formatCardLoadAmountLabel,
  handleAmountKeyDown,
  handleAmountPaste,
  isValidCardLoadAmount,
  sanitizeAmountInput,
} from './helper';

import styles from './style.module.scss';

const { Option } = Select;
const { inputMethod: INPUT_METHODS } = AMPLITUDE_WALLET_PROPERTY_VALUES;

const cardLabel = card => `${card.paymentCardType} •••• ${(card.cardNumber || '').slice(-4)}`;

const AddFunds = ({
  walletId,
  // The bank transfer reference the Wallet Service issued for this wallet
  wireReference,
  // The wallet's own currency ('gbp' | 'eur'), not the corporation country: a card load is
  // always in the wallet's currency.
  currency,
  accountDetails = WALLET_BANK_TRANSFER_DETAILS[String(currency ?? '').toLowerCase()] ??
    WALLET_BANK_TRANSFER_DETAILS.gbp,
  bankTransfer,
}) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  const analyticsCurrency = toAnalyticsCurrency(currency);
  const dispatch = useDispatch();
  const paymentsSpa = useSelector(state => state.paymentsSpa);
  const paymentMethods = useMemo(() => paymentsSpa?.data || [], [paymentsSpa]);
  const cardsLoading = paymentsSpa?.loading;
  const cardsError = paymentsSpa?.getError;
  const symbol = WALLET_CURRENCY_SYMBOLS[String(currency ?? '').toLowerCase()] ?? '';
  const currentCorporation = useSelector(state => state.user?.currentCorporation);
  // Net new UK orgs cannot submit a wire transfer, so they only get the card option
  const isWireTransferAvailable = !isUkCorporationCreatedAfterWalletGoLive({
    country: currentCorporation?.country,
    createdAt: currentCorporation?.createdAt ?? null,
  });

  const [tab, setTab] = useState(isWireTransferAvailable ? 'bank' : 'card');
  const [presetMajor, setPresetMajor] = useState(CARD_LOAD_DEFAULT_PRESET_MAJOR);
  const [otherAmount, setOtherAmount] = useState('');
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const [paySelectOpen, setPaySelectOpen] = useState(false);
  const [confirmCardPaymentModalOpen, setConfirmCardPaymentModalOpen] = useState(false);
  // Card ids present when "Add a new card" was clicked; any card outside it was added since.
  const cardIdsBeforeAddRef = useRef(null);

  const selectedCard = paymentMethods.find(card => card.id === selectedCardId);

  // A typed amount wins over the highlighted preset; the Wallet Service takes minor units.
  const amountMajor = otherAmount !== '' ? Number(otherAmount) : presetMajor;
  const amountMinor = toMinorUnits(amountMajor);
  const isAmountValid = isValidCardLoadAmount(amountMinor);
  const amountLabel = isAmountValid ? formatCardLoadAmountLabel(amountMinor, currency) : '';

  useEffect(() => {
    dispatch({ type: paymentsActions.GET });
  }, [dispatch]);

  // The saved cards are persisted redux state, so a reload can rehydrate a card the refetch then
  // drops. Falling back keeps the selection pointing at a card that exists.
  useEffect(() => {
    if (!paymentMethods.length) {
      return;
    }
    if (!paymentMethods.some(card => card.id === selectedCardId)) {
      setSelectedCardId(paymentMethods[0].id);
    }
  }, [paymentMethods, selectedCardId]);

  const sendAmountSelected = (amount, inputMethod) =>
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletAddFundsAmountSelected, {
      amount,
      currency: analyticsCurrency,
      inputMethod,
    });

  const handlePresetClick = preset => {
    setPresetMajor(preset);
    setOtherAmount('');
    sendAmountSelected(preset, INPUT_METHODS.preset);
  };

  // Reported on blur rather than per keystroke
  const handleOtherAmountBlur = () => {
    if (otherAmount !== '' && !Number.isNaN(Number(otherAmount))) {
      sendAmountSelected(Number(otherAmount), INPUT_METHODS.custom);
    }
  };

  const handleCardChange = cardId => {
    setSelectedCardId(cardId);
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletAddFundsPaymentCardSelected, {
      isNewCard: !!cardIdsBeforeAddRef.current && !cardIdsBeforeAddRef.current.has(cardId),
    });
  };

  const handleAddNewCardClick = () => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletAddFundsNewCardButtonClicked);
    cardIdsBeforeAddRef.current = new Set(paymentMethods.map(card => card.id));
    setPaySelectOpen(false);
    setAddCardModalOpen(true);
  };

  const handleCardCtaClick = () => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletAddFundsCardCtaClicked, {
      amount: amountMajor,
      currency: analyticsCurrency,
    });
    setConfirmCardPaymentModalOpen(true);
  };

  const handleOtherAmountChange = event => {
    const value = sanitizeAmountInput(event.target.value);
    setOtherAmount(value);
    setPresetMajor(value === '' ? CARD_LOAD_DEFAULT_PRESET_MAJOR : null);
  };

  const resetAmount = () => {
    setPresetMajor(CARD_LOAD_DEFAULT_PRESET_MAJOR);
    setOtherAmount('');
  };

  let cardPlaceholder;
  if (cardsError) {
    cardPlaceholder = translateText('corporateAccounts.wallet.dashboard.addFunds.cardsError');
  } else if (cardsLoading) {
    cardPlaceholder = translateText('corporateAccounts.wallet.dashboard.addFunds.loadingCards');
  } else {
    cardPlaceholder = translateText('corporateAccounts.wallet.dashboard.addFunds.selectCard');
  }

  return (
    <div className={styles.card} data-testid="walletAddFunds">
      <div className={styles.cardHeader}>
        <span className={`${styles.cardHeaderIcon} ${styles.cardHeaderIconAccent}`}>
          <DollarOutlined />
        </span>
        {translateText('corporateAccounts.wallet.dashboard.addFunds.title')}
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardCopy}>
          {translateText('corporateAccounts.wallet.dashboard.addFunds.copy')}
        </div>

        {isWireTransferAvailable && (
          <div className={styles.tabs}>
            <button
              type="button"
              className={classNames(styles.tab, { [styles.tabActive]: tab === 'bank' })}
              onClick={() => setTab('bank')}
              data-testid="walletAddFundsTabBank"
            >
              {translateText('corporateAccounts.wallet.dashboard.addFunds.tabBank')}
            </button>
            <button
              type="button"
              className={classNames(styles.tab, { [styles.tabActive]: tab === 'card' })}
              onClick={() => setTab('card')}
              data-testid="walletAddFundsTabCard"
            >
              {translateText('corporateAccounts.wallet.dashboard.addFunds.tabCard')}
            </button>
          </div>
        )}

        {tab === 'card' || !isWireTransferAvailable ? (
          <>
            <div className={styles.amountRow}>
              {CARD_LOAD_PRESETS_MAJOR.map(preset => (
                <button
                  type="button"
                  key={preset}
                  className={classNames(styles.amountPreset, {
                    [styles.selected]: presetMajor === preset && otherAmount === '',
                  })}
                  onClick={() => handlePresetClick(preset)}
                  data-testid={`walletAddFundsPreset-${preset}`}
                >
                  {formatCardLoadAmountLabel(toMinorUnits(preset), currency)}
                </button>
              ))}
              <AmountInput
                currencyCode={currency}
                symbol={symbol}
                label={translateText(
                  'corporateAccounts.wallet.dashboard.lowBalanceAlert.otherPlaceholder',
                )}
                selected={otherAmount !== ''}
                value={otherAmount}
                inputMode="decimal"
                onChange={handleOtherAmountChange}
                onBlur={handleOtherAmountBlur}
                onKeyDown={handleAmountKeyDown}
                onPaste={handleAmountPaste}
                data-testid="walletAddFundsOtherAmount"
              />
            </div>

            <div className={styles.fieldLabel}>
              {translateText('corporateAccounts.wallet.dashboard.addFunds.payWith')}
            </div>
            <div className={styles.paySelectWrap} data-testid="walletAddFundsPaySelect">
              <Select
                className={styles.paySelect}
                popupClassName={styles.payDropdown}
                optionLabelProp="label"
                // antd renders the raw value when no option matches it, showing the card's id
                value={selectedCard ? selectedCardId : undefined}
                placeholder={cardPlaceholder}
                loading={Boolean(cardsLoading)}
                onChange={handleCardChange}
                getPopupContainer={trigger => trigger.parentNode}
                virtual={false}
                open={paySelectOpen}
                onDropdownVisibleChange={setPaySelectOpen}
                // The "Add a new card" action (below, via dropdownRender) already covers
                // the no-saved-cards case. `notFoundContent={null}` would suppress the
                // whole popup (including that action) when there are zero cards — use an
                // empty-but-truthy node instead so only the "No data" text disappears.
                notFoundContent={<span style={{ display: 'none' }} />}
                dropdownRender={menu => (
                  <>
                    {menu}
                    <Can I="create" a="PaymentSpa" passThrough>
                      {can => (
                        <button
                          type="button"
                          className={styles.payUseNewCard}
                          disabled={!can}
                          data-testid="walletAddFundsUseNewCard"
                          onMouseDown={event => event.preventDefault()}
                          onClick={handleAddNewCardClick}
                        >
                          {translateText('corporateAccounts.wallet.dashboard.addFunds.addNewCard')}
                        </button>
                      )}
                    </Can>
                  </>
                )}
              >
                {paymentMethods.map(card => (
                  <Option
                    key={card.id}
                    value={card.id}
                    title={cardLabel(card)}
                    label={
                      <span className={styles.payTriggerLabel}>
                        <CreditCardOutlined />
                        {cardLabel(card)}
                      </span>
                    }
                  >
                    {cardLabel(card)}
                  </Option>
                ))}
              </Select>
            </div>

            <div className={styles.addCtaWrap}>
              <Can I="create" a="PaymentSpa" passThrough>
                {can => (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    className={styles.addCta}
                    disabled={!can || !selectedCard || !isAmountValid}
                    onClick={handleCardCtaClick}
                    data-testid="walletAddFundsCta"
                  >
                    {translateText('corporateAccounts.wallet.dashboard.addFunds.cta', {
                      amount: amountLabel,
                    })}
                  </Button>
                )}
              </Can>
            </div>
          </>
        ) : (
          <div data-testid="walletAddFundsBank">
            <div className={styles.bankCopy}>
              {translateText('corporateAccounts.wallet.dashboard.addFunds.bankCopy')}
            </div>

            <div className={styles.addCtaWrap}>
              <Can I="create" a="PaymentSpa" passThrough>
                {can => (
                  <Button
                    type="primary"
                    className={styles.addCta}
                    disabled={!can}
                    data-testid="walletAddFundsBankCta"
                    onClick={bankTransfer.openSend}
                  >
                    {translateText('corporateAccounts.wallet.dashboard.addFunds.bankCta')}
                  </Button>
                )}
              </Can>
            </div>
          </div>
        )}
      </div>
      <SendTransferModal
        visible={bankTransfer.step === BANK_STEPS.send}
        onClose={bankTransfer.close}
        onNext={bankTransfer.goToConfirm}
        onCopyBankDetail={bankTransfer.onCopyBankDetail}
        bankDetails={accountDetails}
        wireReference={wireReference}
      />
      <ConfirmTransferModal
        visible={bankTransfer.step === BANK_STEPS.confirm}
        onClose={bankTransfer.close}
        onBack={bankTransfer.goBack}
        onSubmitted={bankTransfer.onSubmitted}
        currencySymbol={symbol}
        currency={currency}
        walletId={walletId}
        wireReference={wireReference}
      />
      <AddCardModal visible={addCardModalOpen} onClose={() => setAddCardModalOpen(false)} />
      <ConfirmCardPaymentModal
        visible={confirmCardPaymentModalOpen}
        onClose={() => setConfirmCardPaymentModalOpen(false)}
        onSuccess={resetAmount}
        walletId={walletId}
        currency={currency}
        amountMinor={amountMinor}
        amountLabel={amountLabel}
        // The Wallet Service charges the PAS payment account. `card.id` is the corporate-accounts row
        // id (used to key the Select); the PAS id PAS/JMS resolve is `paymentAccountId`.
        card={
          selectedCard
            ? { id: selectedCard.paymentAccountId, label: cardLabel(selectedCard) }
            : null
        }
      />
    </div>
  );
};

export default AddFunds;
